import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchStudentLocales,
  formatLocale,
  localeToIso,
  startScenarioRun,
  startSceneVoiceSession,
} from './api';
import { usePreferredLocale } from './PreferredLocaleContext';
import Voice from './Voice';
import './SceneVoice.css';

// What the cast speaks, which is what the languages on offer are the counterpart to. The seeded
// bakery is French; a scene in another language would have to say so rather than assume.
const CAST_LOCALE = 'fr_FR';

// The status the backend sends back when it needs to be told something before it can act.
const LANGUAGE_NEEDED = 428;

// Speaking to one character of one scenario's current scene, at /scenes/{key}/voice/{role}.
//
// A role rather than a persona: the cast is seeded, and the browser has no business carrying a
// character's UUID around. Starting the run and opening the session are two calls, made one errand
// here — the student clicked a character, not an API.
function SceneVoice() {
  const { scenarioKey, role } = useParams();
  const { t } = useTranslation();
  const { preferredLocale, setPreferredLocale } = usePreferredLocale();
  const [runId, setRunId] = useState(null);
  const [needsLanguage, setNeedsLanguage] = useState(false);
  const [spokenLanguage, setSpokenLanguage] = useState('');
  const [languages, setLanguages] = useState([]);
  const [failed, setFailed] = useState(false);
  // A run is started once per visit, and React mounts an effect twice in development.
  const starting = useRef(false);

  const start = useCallback(
    async (studentLocale) => {
      try {
        const run = await startScenarioRun(scenarioKey, studentLocale);
        setNeedsLanguage(false);
        setRunId(run.runId);
        // The server has just written it to the profile; saying so here spares the rest of the
        // session a refetch to learn what it was told a moment ago.
        if (studentLocale) {
          setPreferredLocale(studentLocale);
        }
      } catch (error) {
        if (error?.status === LANGUAGE_NEEDED) {
          setNeedsLanguage(true);
        } else {
          setFailed(true);
        }
      }
    },
    [scenarioKey, setPreferredLocale],
  );

  useEffect(() => {
    if (starting.current) {
      return;
    }
    starting.current = true;
    // The profile is what the server reads; sending what this tab knows of it only saves the
    // student a question they have already answered somewhere else.
    start(preferredLocale ?? undefined);
    // Deliberately once per visit: a run is a thing that happened, not a thing to re-derive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only once the question is actually being asked: most visits never see it, and the list is the
  // server's answer to what a student may speak to this cast.
  useEffect(() => {
    if (!needsLanguage) {
      return undefined;
    }
    let cancelled = false;
    fetchStudentLocales(CAST_LOCALE)
      .then((locales) => {
        if (cancelled) return;
        setLanguages(locales);
        setSpokenLanguage((chosen) => chosen || (locales[0] ? localeToIso(locales[0]) : ''));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [needsLanguage]);

  const answer = (event) => {
    event.preventDefault();
    const [languageCode, countryCode] = spokenLanguage.split('_');
    start({ languageCode, countryCode });
  };

  if (failed) {
    return <p className="scene-voice-notice">{t('sceneVoice.failed')}</p>;
  }

  if (needsLanguage) {
    return (
      <form className="scene-voice-language" onSubmit={answer}>
        <label htmlFor="scene-voice-spoken">{t('sceneVoice.whatYouSpeak')}</label>
        <select
          id="scene-voice-spoken"
          value={spokenLanguage}
          onChange={(event) => setSpokenLanguage(event.target.value)}
        >
          {languages.map((locale) => {
            const iso = localeToIso(locale);
            return (
              <option key={iso} value={iso}>
                {t(`locales.${iso}`, formatLocale(locale))}
              </option>
            );
          })}
        </select>
        <button type="submit">{t('sceneVoice.continue')}</button>
      </form>
    );
  }

  if (!runId) {
    return <p className="scene-voice-notice">{t('sceneVoice.starting')}</p>;
  }

  return (
    <Voice openSession={() => startSceneVoiceSession(runId, role)} />
  );
}

export default SceneVoice;
